import { createContext, useContext, useState, useMemo } from 'react';

// ─── Tradução - pt e en ────────────────────────────────────────────────────────
export const translations = {
  pt: {
    nav: {
      about: '// Sobre',
      skills: '// Skills',
      projects: '// Projetos',
      experience: '// Experiência',
      contact: '// Contato',
    },
    hero: {
      greeting: 'Olá, eu sou',
      roles: [
        'Desenvolvedor Full Stack',
        'Foco em IA e inovação',
        'React & TypeScript',
        'C# & .NET Backend',
      ],
    },
    about: {
      tag: '// Sobre',
      title: 'Alan Souza.',
      desc: 'Desenvolvedor Full Stack com foco em IA e inovação, com experiência em React/TypeScript, C#/.NET e soluções digitais orientadas a impacto real. Atuando em ambientes financeiros regulados e em projetos de transformação com forte presença em tecnologia e estratégia.',
      btnCV: 'Baixar Currículo',
      stats: [
        { value: 'IA', label: 'Atuação em inovação e soluções com IA' },
        { value: '2×', label: 'Tech Lead em projetos reais (Embrapa & Santa Casa)' },
        { value: 'BACEN', label: 'Experiência em ambiente financeiro regulado' },
        { value: 'Full Stack', label: 'React/TypeScript + C#/.NET em produção' },
      ],
    },
    skills: {
      tag: '// Skills',
      title: 'Conhecimentos .',
      desc: 'Passe o mouse sobre uma tecnologia para ver como ela se conecta ao restante do stack. Frontend, Backend, Banco de dados e Ferramentas — todos ligados em produção.',
      groups: {
        frontend: 'Frontend',
        backend: 'Backend',
        db: 'Banco de Dados',
        tools: 'Ferramentas',
      },
      hintPrefix: 'Conexões:',
    },
    projects: {
      tag: '// Projetos',
      title: 'Destaques .',
      github: 'Ver no GitHub',
      items: [
        {
          category: 'Full-Stack',
          title: 'Simplify Sales Management',
          description: 'Sistema de gestão para vendas com dashboard e fluxo completo para operação comercial.',
          highlights: ['Dashboard', 'Fluxo completo de vendas', 'Backend e frontend integrados'],
        },
        {
          category: 'Full-Stack',
          title: 'Sistema de Estacionamento',
          description: 'Aplicação web para controle de estacionamento com gestão de vagas e operações.',
          highlights: ['Gestão de vagas', 'Fluxo administrativo', 'Frontend e API integrados'],
        },
        {
          category: 'Mobile',
          title: 'Personal Tasks',
          description: 'Aplicativo mobile para organização pessoal com foco em produtividade.',
          highlights: ['Interface simples', 'Organização de tarefas', 'Experiência mobile fluida'],
        },
        {
          category: 'API',
          title: 'API REST Spring',
          description: 'API robusta construída com Spring Boot para exposição de serviços e regras de negócio.',
          highlights: ['Endpoints REST', 'Arquitetura limpa', 'Boa documentação e manutenção'],
        },
        {
          category: 'Game/Full-Stack',
          title: 'Sudoku React',
          description: 'Jogo interativo de Sudoku com interface responsiva e lógica de jogo.',
          highlights: ['Interface responsiva', 'Jogabilidade fluida', 'Estrutura simples e escalável'],
        },
        {
          category: 'Frontend',
          title: 'ToDo React',
          description: 'Lista de tarefas com interface moderna construída em React.',
          highlights: ['CRUD simples', 'Experiência agradável', 'Código limpo e reutilizável'],
        },
        {
          category: 'Frontend/API',
          title: 'Price Scan API',
          description: 'API para escaneamento e comparação de preços com integração de serviços.',
          highlights: ['Integração de dados', 'Estrutura para consumo', 'Escalabilidade em mente'],
        },
      ],
    },
    experience: {
      tag: '// Experiência Profissional',
      title: 'Experiência .',
      typeLabels: {
        work: 'Profissional',
        extension: 'Extensão',
      },
      tags: {
        current: 'Profissional Atual',
        ongoing: 'Em andamento',
        done: 'Concluído',
      },
      items: [
        {
          company: 'Grupo Multiplica',
          role: 'Analista de IA em Inovação',
          period: 'Atual',
          location: 'São Carlos, SP',
          summary: 'Atuação na área de inovação com foco em inteligência artificial, desenvolvimento de soluções e apoio à transformação digital da organização.',
          bullets: [
            'Desenvolvimento de soluções com foco em IA e inovação',
            'Apoio estratégico em iniciativas tecnológicas e digitais',
            'Integração entre tecnologia, negócios e experiência de usuário',
          ],
        },
        {
          company: 'Luz Soluções Financeiras',
          role: 'Desenvolvedor Full Stack',
          period: 'Fev 2026 – Atual',
          location: 'Presencial · São Carlos, SP',
          summary: 'Desenvolvimento e manutenção de funcionalidades full stack em plataforma regulada pelo BACEN, garantindo integridade e confiabilidade das operações financeiras.',
          bullets: [
            'Otimização de queries no PostgreSQL e refatoração de endpoints C# para execução assíncrona (async/await)',
            'Atuação em squads Scrum com code reviews orientados aos princípios SOLID',
            'Construção de APIs REST seguras e interfaces eficientes sob metodologias ágeis',
          ],
        },
        {
          company: 'Santa Casa de São Carlos – Projeto de Extensão IFSP',
          role: 'Tech Lead de Frontend',
          period: 'Jan 2026 – Atual',
          location: 'Projeto Acadêmico · São Carlos, SP',
          summary: 'Liderança técnica do frontend de uma aplicação web de gerenciamento de pacientes e medicamentos para ambiente hospitalar real.',
          bullets: [
            'Arquitetura e desenvolvimento com React e Next.js integrado a backend Java',
            'Revisão e validação de pull requests, coordenação de cards via GitHub Projects Kanban',
            'Orientação da equipe sobre arquitetura, boas práticas e padrões de código',
          ],
        },
        {
          company: 'Fifteen Hundred',
          role: 'Estagiário de Desenvolvimento Full Stack',
          period: 'Dez 2024 – Jan 2026',
          location: 'Remoto',
          summary: 'Participação ativa em projetos full stack com foco em qualidade e segurança.',
          bullets: [
            'Desenvolvimento de componentes visuais com React e Tailwind CSS com aderência ao design',
            'Implementação de testes unitários com JUnit, alcançando 90% de cobertura em funcionalidades críticas',
            'Integração de APIs REST e gerenciamento de fluxos de autenticação JWT com C#',
          ],
        },
        {
          company: 'Embrapa São Carlos – Projeto de Extensão IFSP',
          role: 'Tech Lead de Frontend',
          period: 'Jan 2025 – Jun 2025',
          location: 'Projeto Acadêmico · São Carlos, SP',
          summary: 'Entrega de sistema web completo para gerenciamento de talhões agropecuários e pecuários para cliente real.',
          bullets: [
            'Desenvolvimento com React, Next.js e backend Java sob metodologia Scrum',
            'Coordenação de distribuição de tarefas via GitHub Projects Kanban',
            'Orientação da equipe por meio de code reviews e resolução de problemas técnicos',
          ],
        },
        {
          company: 'Lojas Quero-Quero S.A.',
          role: 'Estagiário de Desenvolvimento Web',
          period: 'Fev 2024 – Jun 2024',
          location: 'Remoto',
          summary: 'Levantamento de requisitos, implementação de funcionalidades web e comunicação direta com stakeholders.',
          bullets: [
            'Tradução de requisitos de negócio em especificações técnicas claras para o time de desenvolvimento',
            'Participação em entregas com foco em experiência do usuário',
            'Trabalho com definição de interfaces e fluxo de produto',
          ],
        },
        {
          company: 'FESC – Fundação Educacional São Carlos',
          role: 'Estagiário & Professor de Programação',
          period: 'Mai 2023 – Jan 2024',
          location: 'Presencial · São Carlos, SP',
          summary: 'Ministrei aulas de programação para crianças, jovens, adultos e idosos em situação de marginalização social.',
          bullets: [
            'Desenvolvi abordagens didáticas flexíveis e materiais adaptados para diferentes faixas etárias',
            'Trabalho com inclusão digital e ensino de programação',
            'Desenvolvimento de metodologias de ensino inclusivas',
          ],
        },
      ],
    },
    contact: {
      tag: '// Contato',
      title: 'Vamos conversar.',
      subtitle: 'Me fale sobre o seu projeto',
      desc: 'Vamos construir algo incrível juntos!',
      formTitle: 'Envie uma mensagem 🚀',
      name: 'Seu nome*',
      email: 'E-mail*',
      subject: 'Assunto',
      message: 'Conte-me mais sobre seu projeto*',
      send: 'Enviar mensagem',
      sending: 'Enviando...',
      success: 'Mensagem enviada com sucesso!',
      error: 'Erro ao enviar. Tente novamente.',
    },
    footer: {
      cvLabel: 'Baixar currículo',
      built: 'Full Stack · React · C#/.NET',
    },
  },
  en: {
    nav: {
      about: '// About',
      skills: '// Skills',
      projects: '// Projects',
      experience: '// Experience',
      contact: '// Contact',
    },
    hero: {
      greeting: 'Hello, I am',
      roles: [
        'Full Stack Developer',
        'Focused on AI and innovation',
        'React & TypeScript',
        'C# & .NET Backend',
      ],
    },
    about: {
      tag: '// About',
      title: 'Alan Souza.',
      desc: 'Full Stack Developer focused on AI and innovation, with experience in React/TypeScript, C#/.NET and digital solutions oriented to real impact. Working in regulated financial environments and transformation projects with a strong connection between technology and strategy.',
      btnCV: 'Download Resume',
      stats: [
        { value: 'AI', label: 'Work in innovation and AI-driven solutions' },
        { value: '2×', label: 'Tech Lead on real projects (Embrapa & Santa Casa)' },
        { value: 'BACEN', label: 'Experience in regulated financial environments' },
        { value: 'Full Stack', label: 'React/TypeScript + C#/.NET in production' },
      ],
    },
    skills: {
      tag: '// Skills',
      title: 'Knowledge .',
      desc: 'Hover over a technology to see how it connects to the rest of the stack. Frontend, backend, databases, and tools — all tied together in production.',
      groups: {
        frontend: 'Frontend',
        backend: 'Backend',
        db: 'Database',
        tools: 'Tools',
      },
      hintPrefix: 'Connections:',
    },
    projects: {
      tag: '// Projects',
      title: 'Highlights .',
      github: 'View on GitHub',
      items: [
        {
          category: 'Full-Stack',
          title: 'Simplify Sales Management',
          description: 'Sales management system with a dashboard and complete commercial workflow.',
          highlights: ['Dashboard', 'Complete sales workflow', 'Frontend and backend integration'],
        },
        {
          category: 'Full-Stack',
          title: 'Parking System',
          description: 'Web application for parking management with spot and operation control.',
          highlights: ['Spot management', 'Administrative workflow', 'Frontend and API integration'],
        },
        {
          category: 'Mobile',
          title: 'Personal Tasks',
          description: 'Mobile app for personal organization focused on productivity.',
          highlights: ['Simple interface', 'Task organization', 'Smooth mobile experience'],
        },
        {
          category: 'API',
          title: 'Spring REST API',
          description: 'Robust API built with Spring Boot for exposing services and business rules.',
          highlights: ['REST endpoints', 'Clean architecture', 'Good documentation and maintenance'],
        },
        {
          category: 'Game/Full-Stack',
          title: 'Sudoku React',
          description: 'Interactive Sudoku game with responsive interface and game logic.',
          highlights: ['Responsive interface', 'Smooth gameplay', 'Simple and scalable structure'],
        },
        {
          category: 'Frontend',
          title: 'ToDo React',
          description: 'Task list with a modern UI built in React.',
          highlights: ['Simple CRUD', 'Enjoyable experience', 'Clean and reusable code'],
        },
        {
          category: 'Frontend/API',
          title: 'Price Scan API',
          description: 'API for price scanning and comparison with service integration.',
          highlights: ['Data integration', 'Consumer-ready structure', 'Scalability in mind'],
        },
      ],
    },
    experience: {
      tag: '// Professional Experience',
      title: 'Experience .',
      typeLabels: {
        work: 'Professional',
        extension: 'Extension',
      },
      tags: {
        current: 'Current role',
        ongoing: 'Ongoing',
        done: 'Completed',
      },
      items: [
        {
          company: 'Grupo Multiplica',
          role: 'AI & Innovation Analyst',
          period: 'Present',
          location: 'São Carlos, SP',
          summary: 'Working in innovation with a focus on artificial intelligence, solution development, and support for the organization’s digital transformation.',
          bullets: [
            'Developing AI-focused solutions and innovation initiatives',
            'Supporting strategic technology and digital transformation efforts',
            'Connecting technology, business, and user experience',
          ],
        },
        {
          company: 'Luz Soluções Financeiras',
          role: 'Full Stack Developer',
          period: 'Feb 2026 – Present',
          location: 'On-site · São Carlos, SP',
          summary: 'Developed and maintained full-stack features for a BACEN-regulated platform, ensuring the integrity and reliability of financial operations.',
          bullets: [
            'Optimized PostgreSQL queries and refactored C# endpoints for asynchronous execution (async/await)',
            'Worked in Scrum squads with code reviews aligned to SOLID principles',
            'Built secure REST APIs and efficient interfaces under agile methodologies',
          ],
        },
        {
          company: 'Santa Casa de São Carlos – IFSP Extension Project',
          role: 'Frontend Tech Lead',
          period: 'Jan 2026 – Present',
          location: 'Academic project · São Carlos, SP',
          summary: 'Technical leadership of the frontend for a web application for managing patients and medications in a real hospital environment.',
          bullets: [
            'Architected and developed the frontend with React and Next.js connected to a Java backend',
            'Reviewed and validated pull requests and coordinated cards via GitHub Projects Kanban',
            'Guided the team on architecture, best practices, and coding standards',
          ],
        },
        {
          company: 'Fifteen Hundred',
          role: 'Full Stack Development Intern',
          period: 'Dec 2024 – Jan 2026',
          location: 'Remote',
          summary: 'Actively contributed to full-stack projects with a focus on quality and security.',
          bullets: [
            'Developed visual components with React and Tailwind CSS while ensuring design consistency',
            'Implemented unit tests with JUnit, reaching 90% coverage in critical system features',
            'Integrated REST APIs and managed JWT authentication flows with C#',
          ],
        },
        {
          company: 'Embrapa São Carlos – IFSP Extension Project',
          role: 'Frontend Tech Lead',
          period: 'Jan 2025 – Jun 2025',
          location: 'Academic project · São Carlos, SP',
          summary: 'Delivered a complete web system for managing agricultural and livestock plots for a real client.',
          bullets: [
            'Developed the solution with React, Next.js, and a Java backend using Scrum',
            'Coordinated task distribution through GitHub Projects Kanban',
            'Mentored the team through code reviews and technical problem-solving',
          ],
        },
        {
          company: 'Lojas Quero-Quero S.A.',
          role: 'Web Development Intern',
          period: 'Feb 2024 – Jun 2024',
          location: 'Remote',
          summary: 'Collected requirements, implemented web features, and communicated directly with stakeholders.',
          bullets: [
            'Translated business requirements into clear technical specifications for the development team',
            'Participated in deliveries focused on user experience',
            'Worked on interface definition and product flow',
          ],
        },
        {
          company: 'FESC – Fundação Educacional São Carlos',
          role: 'Programming Instructor Intern',
          period: 'May 2023 – Jan 2024',
          location: 'On-site · São Carlos, SP',
          summary: 'Taught programming to children, teenagers, adults, and seniors in socially marginalized communities.',
          bullets: [
            'Developed flexible teaching approaches and adapted materials for different age groups',
            'Worked on digital inclusion and programming education',
            'Created inclusive teaching methodologies',
          ],
        },
      ],
    },
    contact: {
      tag: '// Contact',
      title: "Let's talk.",
      subtitle: 'Tell me about your project',
      desc: "Let's build something great together!",
      formTitle: 'Send a message 🚀',
      name: 'Your name*',
      email: 'E-mail*',
      subject: 'Subject',
      message: 'Tell me more about your project*',
      send: 'Send message',
      sending: 'Sending...',
      success: 'Message sent successfully!',
      error: 'Error sending. Please try again.',
    },
    footer: {
      cvLabel: 'Download resume',
      built: 'Full Stack · React · C#/.NET',
    },
  },
};

// ─── Context ──────────────────────────────────────────────────────────────────
const LangContext = createContext(null);

/**
 * ✅ Provider melhorado com memoização
 * Previne re-renders desnecessários em componentes que usam useLang()
 */
export function LangProvider({ children }) {
  const [lang, setLang] = useState('pt');
  
  // Obter traduções da linguagem atual
  const t = translations[lang];
  
  // Toggle entre pt e en
  const toggle = () => setLang(l => (l === 'pt' ? 'en' : 'pt'));

  /**
   * ✅ Memoizar o valor do context
   * Evita que todos os componentes consumers sejam re-renderizados
   * quando apenas o estado de 'lang' muda
   */
  const value = useMemo(
    () => ({ lang, t, toggle }),
    [lang, t]
  );

  return (
    <LangContext.Provider value={value}>
      {children}
    </LangContext.Provider>
  );
}

/**
 * ✅ Custom hook com validação
 * Garante que o context está sendo usado corretamente
 */
export function useLang() {
  const context = useContext(LangContext);
  
  if (!context) {
    throw new Error(
      'useLang deve ser usado dentro de um <LangProvider>. ' +
      'Certifique-se de envolver seu app com <LangProvider> em App.jsx'
    );
  }
  
  return context;
}

/**
 * ✅ Hook alternativo para acessar apenas a linguagem
 * Útil para componentes que só precisam saber a linguagem atual
 */
export function useCurrentLang() {
  const { lang } = useLang();
  return lang;
}

/**
 * ✅ Hook para acessar apenas as traduções
 * Memoizado para máxima performance
 */
export function useTranslations() {
  const { t } = useLang();
  return t;
}